// Two separate palettes live in this file, and the distinction matters.
//
// THEME_PRESETS mirrors the *product's* real visual registers — the looks a
// customer's finished video actually renders in. The hex values are copied
// verbatim from the render engine's theme table, so the style gallery on the
// landing page is a truthful preview rather than marketing invention.
//
// PAGE is the landing page's own chrome — neon on black. It is deliberately
// NOT one of the video themes: the site is the workshop, the themes are the
// output, and conflating the two would make every video preview look like it
// inherited the site's accent.

export const THEME_PRESETS = [
  {
    id: "investigation",
    headline: ["What they", "knew, and when"],
    label: "Investigation",
    blurb: "Exposés, scandals, disasters — and the critical take on film, games and culture. Cold near-black, alarm red, heavy condensed headings.",
    bestFor: "Investigations & culture",
    backgroundColor: "#0B0C0F",
    accentColor: "#E5342B",
    textColor: "#F2F2F0",
    mutedTextColor: "#8B8F96",
    surface: "grid",
  },
  {
    id: "editorial-light",
    headline: ["The considered", "argument"],
    label: "Editorial",
    blurb: "Essays, profiles, society — and history told as how we got here. Bright off-white broadsheet page with a high-contrast serif. The one light register.",
    bestFor: "Essays & history",
    backgroundColor: "#FAF7F2",
    accentColor: "#B3271E",
    textColor: "#16151A",
    mutedTextColor: "#5A5852",
    surface: "paper",
  },
  {
    id: "keynote",
    headline: ["The numbers", "behind the pitch"],
    label: "Keynote",
    blurb: "Companies, products, markets — and the engineering underneath them. Blue-black with glass surfaces, built for charts and figures.",
    bestFor: "Business & engineering",
    backgroundColor: "#0E1424",
    accentColor: "#4C8DFF",
    textColor: "#FFFFFF",
    mutedTextColor: "#93A4C0",
    surface: "glass",
  },
  {
    id: "nature-field",
    headline: ["What the", "landscape holds"],
    label: "Field",
    blurb: "Climate, biology, food, geography — and the physics and scale behind them. Deep forest green with warm amber, soft organic backdrop and no straight lines.",
    bestFor: "Nature & science",
    backgroundColor: "#12251C",
    accentColor: "#E8B44A",
    textColor: "#F4F1E8",
    mutedTextColor: "#A3AF9E",
    surface: "grid",
  },
  // ---------------------------------------------------------------------
  // NOT A VIDEO REGISTER. Everything above is a look a customer's video can
  // actually render in; this one exists solely because the DASHBOARD's light
  // theme derives its tokens from a THEME_PRESETS entry — see
  // DASHBOARD_LIGHT_PRESET_ID in dashboardTheme.js, which borrows it through
  // themeById() and runs it through the same contrast-verified math.
  //
  // It is kept HERE rather than inlined in dashboardTheme.js so that the light
  // dashboard and the landing page's light sections stay derived by one code
  // path. Deleting it silently breaks the dashboard rather than erroring:
  // themeById falls back to THEME_PRESETS[0], so every "light" token would be
  // derived from investigation's near-black (measured: 16 contrast failures).
  //
  // The landing gallery never shows it — DiagramGallery filters THEME_PRESETS
  // against DIAGRAM_SUBJECTS, which only carries the four registers above.
  // ---------------------------------------------------------------------
  {
    id: "paper-mint",
    headline: ["The system", "behind the results"],
    label: "Field Notes",
    blurb: "Dashboard light theme. Pale sage paper and deep green ink, closer to a lab notebook than an archive.",
    bestFor: "Dashboard only",
    backgroundColor: "#EAF1EC",
    accentColor: "#1F7A5C",
    textColor: "#12211C",
    mutedTextColor: "#5B6E66",
    surface: "paper",
  },
];

// FOUR VIDEO REGISTERS, down from eleven listed here (plus the dashboard-only
// entry noted above). `vox-dark`, `archival`, `tech-mono` and `kurzgesagt` were
// merged into the four by the renderer (see RETIRED_THEME_IDS in
// schema/theme.ts). `studio-slate` and `linen-clay` were marketing-only — the
// renderer never had presets for them, so the page was advertising styles no
// video could be generated in. `minimal-dark`, `vox-light` and `minimal-light`
// went the same way in an earlier cut.
//
// This list exists to be a TRUTHFUL PREVIEW of the registers a customer's video
// can actually render in, so it tracks themeIds in the render engine rather than
// being a gallery of its own. The renderer still ACCEPTS every retired id and
// folds it onto a survivor; the fallback below does the same for any stale id
// reaching the page.
export const themeById = (id) =>
  THEME_PRESETS.find((t) => t.id === id) || THEME_PRESETS[0];

// Landing page chrome — "signal on black". Volt and signal are the only
// saturated colors allowed outside a theme card; everything else is ink,
// panel, bone and mute. Neutrals carry a slight green bias so they read as
// chosen rather than inherited.
//
// This object is also mirrored in src/theme/tokens.cjs, a CommonJS-only
// twin used exclusively by tailwind.config.js (plain Node require(), can't
// load this ES module). An earlier version of this file had PAGE itself
// require() tokens.cjs to avoid the duplication — that worked under Node
// and CRA's production build, but broke under the dev server: webpack's
// per-module wrapping evaluates this require() at a point where it can
// return undefined (surfacing as "Cannot read properties of undefined
// (reading 'volt')" the moment any component read PAGE.volt). A literal,
// hand-kept-in-sync duplicate here is less elegant but is the one form both
// Node (tailwind.config.js) and webpack (every other consumer) load
// reliably. If PAGE changes, update tokens.cjs to match.
//
// Contrast, measured against the surfaces each is actually used on:
//   mute    #B7C0B1 → 10.62:1 on ink, 10.00:1 on panel, 9.49:1 on panelHi (AAA)
//   muteDim #98A094 → 7.02:1 on ink, 6.61:1 on panelHi (AAA)
//   bone    #ECEFE8 → 17.16:1 on ink
//   volt    #C6FF3A → 16.86:1 on ink
export const PAGE = {
  ink: "#08090A",
  inkRaised: "#0C0E10",
  panel: "#101215",
  panelHi: "#15181C",
  line: "rgba(198, 255, 58, 0.14)",
  lineSoft: "rgba(236, 239, 232, 0.08)",
  volt: "#C6FF3A",
  signal: "#39FF88",
  bone: "#ECEFE8",
  mute: "#B7C0B1",
  muteDim: "#98A094",
};

// The marketing landing page's own register — "darkroom", not "terminal".
//
// This is a SEPARATE export rather than a change to PAGE on purpose. PAGE is
// consumed by DashboardStyles, FreeToolsStyles and ui/primitives, and mirrored
// into tailwind.config.js (bg-ink, text-volt, …) which every authed page uses.
// Editing PAGE would re-skin the entire app as a side effect of restyling one
// page. FILM is scoped to `.subscene-landing` and nothing else.
//
// Volt-green-on-black read as a dev tool. This product is a documentary edit
// suite, so the page is lit like one: a warm amber key and a cold cyan fill on
// deep charcoal. The practical payoff is that the brightest thing on screen is
// now a frame of the customer's actual video, which never happened under neon
// green.
//
// Two accents, with a rule that carries the product's own argument:
//   `film` is the INTERFACE accent — CTAs, eyebrows, section markers, rules.
//   `cold` is reserved for DIAGRAM and DATA beats.
// So the photo-vs-diagram distinction is stated in colour, not just in copy.
//
// Contrast, measured (not estimated) against every surface each is used on:
//            ink     inkRaised  panel   panelHi
//   film    11.67      11.37    10.94    10.24
//   cold    10.23       9.97     9.59     8.97
//   bone    16.46      16.04    15.44    14.44
//   mute     7.13       6.95     6.69     6.26
//   muteDim  5.16       5.03     4.84     4.53
//   ink on film (button label) = 11.67
//
// Every foreground clears 4.5:1 on every surface. muteDim was first drafted as
// #7E7A72, which measured 4.34:1 on panel — a fail, and precisely the mistake
// the frontend README documents. Re-run the ratio check if any value here is nudged.
// As with PAGE: text colour is always a token. Never stack opacity on text.
export const FILM = {
  ink: "#0A0A0C",
  inkRaised: "#0E0E11",
  panel: "#131316",
  panelHi: "#1A1A1E",
  line: "rgba(240, 192, 90, 0.14)",
  lineSoft: "rgba(237, 234, 227, 0.08)",
  film: "#F0C05A",
  cold: "#5AC8E8",
  bone: "#EDEAE3",
  mute: "#EDEAE3",
  muteDim: "#868279",
};

// Five steps, replacing the ten near-identical sizes between 9.3px and 12.5px
// that the first pass accumulated. The floor is 12px, and nothing read as a
// sentence sits below 16px.
export const TYPE = {
  micro: "0.75rem",    // 12px — aspect badges, section markers. Peripheral labels only.
  label: "0.8125rem",  // 13px — eyebrows, nav, plan names, runtimes, chips
  action: "0.875rem",  // 14px — every button and filter chip
  bodySm: "1rem",      // 16px — theme blurbs, plan features, tile titles, table cells
  body: "1.0625rem",   // 17px — section ledes
};

// Wide tracking is what destroys legibility at small sizes, so it scales with
// the type instead of being fixed at 0.18em for everything.
export const trackingFor = (fontSize) =>
  fontSize === TYPE.micro ? "0.10em" : "0.16em";

// Font stacks. Two voices: Chakra Petch carries headings/display, Newsreader
// (serif) carries body copy, JetBrains Mono the labels.
//
// These must be applied per element — index.css carries a universal
// `* { font-family: <body face> }` rule that outranks any family set on an
// ancestor, so setting a font on a wrapper silently does nothing. Body is the
// universal default because it outnumbers display ~5:1; anything that is a
// heading must therefore assert `font-display` rather than rely on inheritance.
//
// These are now indirections, not values: the real faces live in ONE place,
// the `:root` block at the top of src/index.css. This object, Tailwind's
// fontFamily and index.css's own rules all resolve to the same variables, so
// changing a typeface is a one-line edit there rather than four in lockstep.
//
// They stay strings shaped like CSS values because every consumer drops them
// straight into a `font-family:` declaration in a template literal
// (LandingStyles.jsx, BlogStyles.jsx) — nothing parses them.
export const FONT = {
  display: "var(--font-display)",
  body: "var(--font-body)",
  mono: "var(--font-mono)",
};
